import type { ApiErrorCode } from '@qlk/shared';

export class LoiApi extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;

  constructor(code: ApiErrorCode, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'LoiApi';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface ThanLoi {
  error?: { code?: ApiErrorCode; message?: string; details?: unknown };
}

/* ------------------------------------------------------------------ */
/* Làm mới phiên                                                       */
/* ------------------------------------------------------------------ */

/**
 * Access token sống **15 phút** (`core/service.ACCESS_TTL_GIAY`), refresh token
 * sống 30 ngày. Trước 26/10/2026 web không gọi `/auth/refresh` ở đâu cả, nên
 * đúng 15 phút sau khi đăng nhập là mọi lượt gọi trả 401 và người dùng bị đá về
 * màn đăng nhập giữa lúc đang làm — kể cả khi họ vừa bấm chuột xong.
 *
 * Nay lớp này tự làm mới **một lần**, rồi gửi lại đúng request vừa hỏng.
 */
export const DUONG_DAN_LAM_MOI = '/api/v1/core/auth/refresh';
const DUONG_DAN_DANG_NHAP = '/api/v1/core/auth/login';

/**
 * Hai đường **không** được thử làm mới.
 *
 * `/auth/login` 401 nghĩa là sai mật khẩu — làm mới một phiên không tồn tại là
 * vô nghĩa và làm thông báo lỗi tới chậm. `/auth/refresh` 401 nghĩa là chính
 * refresh token đã hết hạn; gọi lại nó là vòng lặp.
 */
function boQuaLamMoi(duongDan: string): boolean {
  const dan = duongDan.split('?')[0] ?? duongDan;
  return dan.endsWith(DUONG_DAN_LAM_MOI) || dan.endsWith(DUONG_DAN_DANG_NHAP);
}

/**
 * Lượt làm mới **đang chạy**, dùng chung cho mọi request cùng gặp 401.
 *
 * Một trang mở ra gọi 4–6 endpoint cùng lúc; thiếu biến này thì cả sáu cùng gọi
 * `/auth/refresh`, mà refresh token **xoay vòng** (mỗi lượt thu hồi lượt trước)
 * — năm lượt sau sẽ dùng một token vừa bị thu hồi và đá người dùng ra ngoài.
 */
let dangLamMoi: Promise<boolean> | null = null;

async function goiLamMoi(): Promise<boolean> {
  try {
    const res = await fetch(DUONG_DAN_LAM_MOI, { method: 'POST', credentials: 'same-origin' });
    return res.ok;
  } catch {
    /* Mất mạng giữa chừng: coi như chưa làm mới được, **không** đăng xuất vì
       một lần rớt sóng — chỗ gọi vẫn nhận 401 và tự quyết. */
    return false;
  }
}

function lamMoiPhien(): Promise<boolean> {
  /* `.finally` nhả chốt khi lượt này xong, nên mọi request đã bám vào cùng một
     promise; request **sau đó** mới được mở lượt mới. */
  dangLamMoi ??= goiLamMoi().finally(() => {
    dangLamMoi = null;
  });
  return dangLamMoi;
}

type XuLyHetPhien = () => void;

let xuLyHetPhien: XuLyHetPhien | null = null;

/**
 * Đăng ký việc phải làm khi phiên **hết hẳn**: dọn cache và đưa về màn đăng
 * nhập. Khung ứng dụng đăng ký (xem `main.tsx`), vì chỗ này nằm ngoài React nên
 * không gọi được hook.
 */
export function datXuLyHetPhien(fn: XuLyHetPhien | null): void {
  xuLyHetPhien = fn;
}

/** Chỉ dùng trong test: trả lớp làm mới về trạng thái chưa chạy lượt nào. */
export function datLaiLamMoiPhien(): void {
  dangLamMoi = null;
  xuLyHetPhien = null;
}

/* ------------------------------------------------------------------ */
/* Gọi API                                                             */
/* ------------------------------------------------------------------ */

/**
 * Gọi API nội bộ. Cookie đi kèm tự động (cùng origin nhờ proxy của Vite).
 * Lỗi luôn ném ra dạng `LoiApi` để UI hiển thị thống nhất.
 *
 * Gặp 401 thì **làm mới phiên một lần** rồi gửi lại đúng request đó. Lần gửi
 * lại không làm mới nữa (`daThuLai`): hai lượt 401 liên tiếp nghĩa là phiên hết
 * hẳn, và một vòng lặp thử lại chỉ biến "hết phiên" thành "trang treo".
 */
export async function goiApi<T>(
  duongDan: string,
  init: RequestInit = {},
  daThuLai = false,
): Promise<T> {
  const res = await fetch(duongDan, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401 && !daThuLai && !boQuaLamMoi(duongDan)) {
    if (await lamMoiPhien()) return goiApi<T>(duongDan, init, true);
    xuLyHetPhien?.();
  }

  if (res.status === 401 && daThuLai) xuLyHetPhien?.();

  if (res.status === 204) return undefined as T;

  const kieu = res.headers.get('content-type') ?? '';
  const than: unknown = kieu.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const loi = (than as ThanLoi).error;
    throw new LoiApi(
      loi?.code ?? 'INTERNAL',
      loi?.message ?? 'Không gọi được máy chủ',
      res.status,
      loi?.details,
    );
  }

  return than as T;
}

export const api = {
  get: <T>(duongDan: string) => goiApi<T>(duongDan),
  post: <T>(duongDan: string, than?: unknown) =>
    goiApi<T>(duongDan, {
      method: 'POST',
      ...(than === undefined ? {} : { body: JSON.stringify(than) }),
    }),
  put: <T>(duongDan: string, than?: unknown) =>
    goiApi<T>(duongDan, {
      method: 'PUT',
      ...(than === undefined ? {} : { body: JSON.stringify(than) }),
    }),
  patch: <T>(duongDan: string, than?: unknown) =>
    goiApi<T>(duongDan, {
      method: 'PATCH',
      ...(than === undefined ? {} : { body: JSON.stringify(than) }),
    }),
  /**
   * `delete` là từ khoá nên đặt tên `del`.
   *
   * Nhận `than` cho những lần xoá cần kèm dữ liệu — xoá hợp đồng Network bắt
   * nhập lý do và cách xử lý từng kênh đang gắn.
   */
  del: <T>(duongDan: string, than?: unknown) =>
    goiApi<T>(duongDan, {
      method: 'DELETE',
      ...(than === undefined ? {} : { body: JSON.stringify(than) }),
    }),
};
